<template>
  <transition name="basic">
    <app-loading-spinner v-if="spinning" />
  </transition>
  <div v-click-outside="outside" class="bixeUserSelect">
    <button
      v-if="showIndicator"
      ref="indicator"
      class="selectedUser"
      :class="[showingUser ? 'hasUser' : 'noUser']"
      :disabled="!phaseStore.settingUp"
      @click.prevent="toggleList"
    >
      <div v-if="showingUser" class="userPic">
        <img v-if="showingUser.picture_url" :src="showingUser.picture_url" :alt="showingUser.first_name" />
        <span class="initials" v-else>
          <transition name="basic">
            <app-scaled-text v-if="!changing">
              {{ showingUser.initials }}
            </app-scaled-text>
          </transition>
        </span>
      </div>
    </button>
    <transition name="scale">
      <microskill-user-list v-if="showList" ref="list" @close="showList = false" @outside="outside" :tail="true" />
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import _ from 'underscore';

import { storeToRefs } from 'pinia';
import AppLoadingSpinner from '@/components/AppLoadingSpinner';
import MicroskillUserList from './MicroskillUserList.vue';
import { useUiAnimations } from '../composables/useUiAnimations';
import AppScaledText from '@/components/AppScaledText.vue';
import { useUsersStore } from '@/store/pinia/users';
import { useSidebarStore } from '@/store/pinia/sidebar';
import { usePhaseStore } from '@/store/pinia/phase';

const usersStore = useUsersStore();
const phaseStore = usePhaseStore();
const sidebarStore = useSidebarStore();

const { hiddenUpdate } = useUiAnimations();

const showingUser = ref(false);
const currentSpeaker = ref(false);
const showList = ref(false);
const debouncedChangeAvatar = _.debounce(changeAvatar, 200);
const spinning = ref(false);
const changing = ref(false);

const indicator = ref(null);

onMounted(() => {
  nextTick(() => {
    if (showIndicator.value && indicator.value) {
      indicator.value.style.display = 'block';
    }
  });
});

const { usersOnline: users, speaking, iAmStillAway, iAmStillAwayInGroup } = storeToRefs(usersStore);
const { randomSpeakerTs, pendingSetupSpeaker, settingUp } = storeToRefs(phaseStore);
const { current: currentSidebar } = storeToRefs(sidebarStore);

const speakingId = computed(() => (speaking.value ? speaking.value.id : false));
const showIndicator = computed(
  () =>
    currentSidebar.value?.barType !== 'info' &&
    currentSidebar.value?.phase !== 'breakTime' &&
    !iAmStillAway.value &&
    !iAmStillAwayInGroup.value &&
    !(currentSidebar.value?.phase === 'groups' && !settingUp.value),
);

watch(speaking, debouncedChangeAvatar, { immediate: true });
watch(settingUp, debouncedChangeAvatar);
watch(randomSpeakerTs, (nv) => {
  if (nv) {
    randomAnimation(pendingSetupSpeaker.value);
  }
});

function toggleList() {
  showList.value = !showList.value;
}

function randomAnimation(userId) {
  let ids = users.value.map((u) => u.id);
  let extra = ids.slice(0, ids.indexOf(userId) + 1);
  let list = [];
  let mainDuration = 800;
  if (ids.length < 3) {
    list = [...ids, ...ids, ...ids, ...extra];
  } else if (ids.length < 5) {
    list = [...ids, ...ids, ...extra];
  } else {
    list = [...ids, ...extra];
  }

  let startDelay = mainDuration / (list.length - extra.length);
  let addedDelay = 5;

  var swap = (delay) => {
    if (list.length > 1) {
      usersStore.updateRandomTicker(list.shift());
      if (list.length < 7) {
        addedDelay += 10;
      }
      _.delay(() => {
        swap(delay + addedDelay);
      }, delay);
      console.log('delay', delay);
    } else {
      phaseStore.updateSetupSpeaker({ speaker: userId, isRandom: false });
      _.delay(() => {
        usersStore.updateRandomTicker(false);
      }, delay);
      _.delay(() => {
        spinning.value = false;
      }, delay + 400);
    }
  };

  swap(startDelay);
}

function outside() {
  showList.value = false;
}

function changeAvatar() {
  if (currentSpeaker.value !== speakingId.value && indicator.value) {
    changing.value = true;
    hiddenUpdate(
      indicator.value,
      () => {
        return (showingUser.value = speaking.value);
      },
      'scale',
    ).then(() => {
      changing.value = false;
    });

    currentSpeaker.value = speakingId.value;
  }
}
</script>
<style lang="scss" scoped>
.bixeUserSelect {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 3;
}

.loadingSpinner {
  .opening & {
    --energy: var(--c__skillbar-bg-opening-solid);
  }

  .container & {
    --energy: var(--c__skillbar-bg-container-solid);
  }

  .tension & {
    --energy: var(--c__skillbar-bg-tension-solid);
  }

  .breakthrough & {
    --energy: var(--c__skillbar-bg-breakthrough-solid);
  }

  margin-left: rem(18px);
  border-radius: 50%;
  height: rem(87px);
  width: rem(87px);
  z-index: 5;
  position: absolute;
  right: -2px;
  top: -2px;
  color: var(--c__bg);
  background: var(--energy);

  :deep(path) {
    stroke-width: 2;
  }
}

.selectedUser {
  position: relative;
  margin-left: rem(18px);
  background: var(--c__bg);
  border-radius: 50%;
  height: rem(83px);
  width: rem(83px);
  box-shadow: 0 0 4px rgba(black, 0.2);
  padding: 5px;
  border: solid 2px var(--c__avatar-border-edge);
  z-index: 3;
  &.noUser {
    display: none;
  }
  .userPic {
    position: absolute;
    top: 5px;
    bottom: 5px;
    left: 5px;
    right: 5px;
    img {
      background: var(--grad__initials);
    }
  }
  img,
  .span {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    display: block;
    position: absolute;
    top: 0;
    left: 0;
  }
  img {
    z-index: 2;
  }
  .initials {
    z-index: 1;
    background: var(--grad__initials);
    display: block;
    border-radius: 50%;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    :deep(svg) {
      display: block;
    }
  }
  .name {
    display: block;
    margin: 0.5em 0 0 0;
    color: var(--c__bg);
    visibility: hidden;
  }
}
</style>
